import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core';
import { APP } from 'src/app/config/app';
import { ITimelineVisibility } from 'src/app/models';
import { ScheduleViewMode, TimelineDaysPreset, TimelineHoursPreset } from 'src/app/models/enums';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { sameSelection, toggleSelection } from '../timelineAxis';
import { images } from 'src/images';

@Component({
  selector: 'app-vehicle-timeline-config',
  templateUrl: './vehicle-timeline-config.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VehicleTimelineConfigComponent {
  readonly viewMode = input(ScheduleViewMode.DAY);
  readonly visibleHours = input(APP.SCHEDULE_ALL_HOURS);
  readonly visibleDays = input(APP.SCHEDULE_ALL_WEEKDAYS);
  readonly applyEventEmitter = output<ITimelineVisibility>();
  readonly closeEventEmitter = output<void>();

  readonly hours = APP.SCHEDULE_ALL_HOURS;
  readonly days = APP.SCHEDULE_ALL_WEEKDAYS;
  readonly presets = Object.values(TimelineHoursPreset);
  readonly dayPresets = Object.values(TimelineDaysPreset);
  public images = images;

  isWeekMode = computed(() => this.viewMode() === ScheduleViewMode.WEEK);
  selectedHours = linkedSignal(() => this.visibleHours());
  selectedDays = linkedSignal(() => this.visibleDays());
  activePreset = computed(() => this.presets.find((preset) => sameSelection(APP.SCHEDULE_HOURS_PRESETS[preset], this.selectedHours())));
  activeDayPreset = computed(() => this.dayPresets.find((preset) => sameSelection(APP.SCHEDULE_DAYS_PRESETS[preset], this.selectedDays())));
  changed = computed(
    () => !sameSelection(this.selectedHours(), this.visibleHours()) || !sameSelection(this.selectedDays(), this.visibleDays()),
  );

  constructor(public literalService: LiteralService) {}

  chipClass(selected: boolean): string {
    return selected
      ? 'border-primary-purple-light! bg-primary-purple-light-2-selected text-primary-purple-light font-bold'
      : 'border-neutrals-grey-02 bg-neutrals-white text-primary-purple font-normal!';
  }

  isSelected(hour: number): boolean {
    return this.selectedHours().includes(hour);
  }

  isLastSelected(hour: number): boolean {
    return this.isSelected(hour) && this.selectedHours().length === 1;
  }

  isDaySelected(day: number): boolean {
    return this.selectedDays().includes(day);
  }

  isLastDaySelected(day: number): boolean {
    return this.isDaySelected(day) && this.selectedDays().length === 1;
  }

  hourLabel(hour: number): string {
    return hour.toString().padStart(2, '0');
  }

  dayLabel(day: number): string {
    return this.literalService.get(`calendar.weekDays.${APP.SCHEDULE_WEEKDAY_KEYS[day]}`, true);
  }

  toggleHour(hour: number) {
    if (!this.isLastSelected(hour)) {
      this.selectedHours.update((hours) => toggleSelection(hours, hour, this.hours));
    }
  }

  toggleDay(day: number) {
    if (!this.isLastDaySelected(day)) {
      this.selectedDays.update((days) => toggleSelection(days, day, this.days));
    }
  }

  selectPreset(preset: TimelineHoursPreset) {
    this.selectedHours.set(APP.SCHEDULE_HOURS_PRESETS[preset]);
  }

  selectDayPreset(preset: TimelineDaysPreset) {
    this.selectedDays.set(APP.SCHEDULE_DAYS_PRESETS[preset]);
  }

  apply() {
    this.applyEventEmitter.emit({ hours: this.selectedHours(), days: this.selectedDays() });
  }

  close() {
    this.closeEventEmitter.emit();
  }
}
